import { ErpDiv, ErpLabelRaw } from './ErpHtml';
import React, { useId, useMemo } from 'react';
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";

// Same-value criterion shared by the checked-state lookup and the array
// removal path so a selected item can always be unselected (reference
// equality alone leaves equal-but-not-identical array entries stuck checked).
const isSameValue = (a: any, b: any): boolean => {
  if (a === b) return true;
  if (typeof a === 'number' && typeof b === 'number') {
    return Number.isNaN(a) && Number.isNaN(b);
  }
  const plainish = (v: any) => Array.isArray(v) || (v !== null && typeof v === 'object'
    && (Object.getPrototypeOf(v) === Object.prototype || Object.getPrototypeOf(v) === null));
  if (plainish(a) && plainish(b)) {
    try {
      return JSON.stringify(a) === JSON.stringify(b);
    } catch {
      return false;
    }
  }
  return false;
};

export interface ErpFormCheckboxProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'value' | 'size' | 'onChange' | 'checked'> {
  checked?: any; // Replaces modelValue
  value?: any;
  uncheckedValue?: any;
  id?: string;
  disabled?: boolean;
  label?: React.ReactNode;
  switch?: boolean;
  inline?: boolean;
  size?: 'sm' | 'lg';
  name?: string;
  onChange?: (value: any, event: React.ChangeEvent<HTMLInputElement>) => void;
  children?: React.ReactNode;
}

export const ErpFormCheckbox: React.FC<ErpFormCheckboxProps> = ({
  checked: modelValue,
  value,
  uncheckedValue,
  id,
  disabled,
  label,
  switch: isSwitch,
  inline,
  size,
  name,
  onChange,
  children,
  className,
  ...props
}) => {
  const sizeClass = isSwitch ? '' : size === 'sm' ? 'size-3.5' : size === 'lg' ? 'size-5' : '';
  const fallbackId = useId();
  // Without a caller-supplied id the label has nothing to point at, so the
  // control loses its accessible name and clicking the label does nothing.
  const controlId = id ?? (label || children ? fallbackId : undefined);

  const isChecked = useMemo(() => {
    if (value !== undefined) {
      return Array.isArray(modelValue)
        ? modelValue.some((v: any) => isSameValue(v, value))
        : isSameValue(modelValue, value);
    }
    // Mirror the emitted value exactly: a scalar model that already equals
    // `uncheckedValue` must render unchecked, not fall through to truthiness.
    if (uncheckedValue !== undefined) return isSameValue(modelValue, uncheckedValue) ? false : !!modelValue;
    return !!modelValue;
  }, [value, modelValue, uncheckedValue]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const checked = e.target.checked;
    let next: any;
    if (value !== undefined && Array.isArray(modelValue)) {
      next = checked
        ? [...modelValue, value]
        : modelValue.filter((v: any) => !isSameValue(v, value));
    } else if (value !== undefined) {
      next = checked ? value : (uncheckedValue ?? false);
    } else {
      next = checked;
    }
    onChange?.(next, e);
  };

  const handleCheckedChange = (checkedState: boolean | string) => {
    // The prop contract claims a real ChangeEvent, so populate the members
    // consumers can actually touch instead of only target/name/id.
    const currentTarget = { checked: checkedState === true, value, name, id: controlId } as HTMLInputElement;
    const mockEvent = {
      target: currentTarget,
      currentTarget,
      preventDefault: () => undefined,
      stopPropagation: () => undefined,
      persist: () => undefined,
      isDefaultPrevented: () => false,
      isPropagationStopped: () => false,
      type: 'change',
      bubbles: true,
      cancelable: true,
      defaultPrevented: false,
      timeStamp: 0,
    } as unknown as React.ChangeEvent<HTMLInputElement>;
    handleChange(mockEvent);
  };

  const Control = isSwitch ? Switch : Checkbox;

  return (
    <ErpDiv className={['relative flex items-center gap-2 rtl:[&>input[aria-hidden=true]]:!transform-none', inline && 'inline-flex', className].filter(Boolean).join(' ')}>
      <Control
        {...(props as any)}
        id={controlId}
        name={name}
        {...(isSwitch ? { size: size ?? 'default' as const } : {})}
        checked={isChecked}
        disabled={disabled}
        onCheckedChange={handleCheckedChange}
        className={['cursor-pointer disabled:cursor-not-allowed', sizeClass].filter(Boolean).join(' ')}
      />
      {(children || label) && (
        <ErpLabelRaw className={disabled ? "text-sm leading-5 text-muted-foreground cursor-not-allowed" : "text-sm leading-5 text-foreground cursor-pointer"} htmlFor={controlId}>
          {children || label}
        </ErpLabelRaw>
      )}
    </ErpDiv>
  );
};
